import React,{useEffect,useRef,useState} from 'react';
import {AppState} from 'react-native';
import {View,Text,TextInput,TouchableOpacity,StyleSheet} from './rn';
import ProfileSection from './ProfileSection';
import {InfoButton,InfoText,useInfo} from './InfoHint';
import {useTheme} from '../theme/ThemeContext';
import {useTranslation} from '../i18n/I18nContext';
import {api} from '../services/api';
import {radius} from '../theme';
import {profileFieldStyle,profilePalette} from '../theme/profileSection';

export type VisitRecord={enabled:boolean;place:string|null;timing:string|null;revision:number};
export type VisitSnapshot={mine:VisitRecord;incoming:VisitRecord|null};

export default function VisitInvitation(props:{token:string;contactId:number;myId:number}) {
  // Parent remounts on identity/contact changes; no account's private wording
  // can flash in another account, even before the next effect is installed.
  return <VisitInvitationSession key={`${props.token}:${props.myId}:${props.contactId}`} {...props}/>;
}
function VisitInvitationSession({token,contactId}:{token:string;contactId:number;myId:number}) {
  const {t}=useTranslation();const colors=useTheme();
  const palette=profilePalette(colors);
  const info=useInfo();
  const [snapshot,setSnapshot]=useState<VisitSnapshot|null>(null);
  const [error,setError]=useState(false),[busy,setBusy]=useState(false);
  const [editing,setEditing]=useState(false),[place,setPlace]=useState(''),[timing,setTiming]=useState('');
  const [revision,setRevision]=useState(0),[refresh,setRefresh]=useState(0);
  const generation=useRef(0),foreground=useRef(AppState.currentState==='active');
  const mounted=useRef(true),saving=useRef(false);
  useEffect(()=>()=>{mounted.current=false;generation.current++;},[]);
  useEffect(()=>{
    const epochRef=generation;
    let timer:ReturnType<typeof setTimeout>|undefined;
    const load=async()=>{
      const epoch=++generation.current;
      if(!foreground.current || saving.current)return;
      try {
        const data=await api.getVisitInvitations(token,contactId);
        if(mounted.current && epoch===generation.current && foreground.current){setSnapshot(data);setError(false);}
      }catch{
        if(mounted.current && epoch===generation.current){setSnapshot(null);setError(true);}
      }finally{
        if(mounted.current && epoch===generation.current && foreground.current)timer=setTimeout(load,15000);
      }
    };
    load();
    const sub=AppState.addEventListener('change',state=>{
      foreground.current=state==='active';generation.current++;clearTimeout(timer);
      setSnapshot(null);setEditing(false);
      if(foreground.current)load();
    });
    return()=>{epochRef.current++;clearTimeout(timer);sub.remove();};
  },[token,contactId,refresh]);
  const begin=()=>{
    if(!snapshot)return;
    setPlace(snapshot.mine.place??t('visit.defaultPlace'));setTiming(snapshot.mine.timing??t('visit.defaultTime'));
    setRevision(snapshot.mine.revision);setEditing(true);
  };
  const save=async(enabled:boolean)=>{
    if(!snapshot || saving.current)return;
    saving.current=true;setBusy(true);generation.current++;
    try {
      await api.saveVisitInvitation(token,contactId,enabled
        ? {enabled:true,revision,place:place.trim(),timing:timing.trim()}
        : {enabled:false,revision:snapshot.mine.revision});
      if(mounted.current){setEditing(false);setSnapshot(null);}
    }catch{
      // An ambiguous response must be reconciled by GET, never retried as PUT.
      if(mounted.current){setEditing(false);setSnapshot(null);setError(true);}
    }finally{
      saving.current=false;
      if(mounted.current){setBusy(false);setRefresh(n=>n+1);}
    }
  };
  const text={color:colors.black,fontSize:16,flexShrink:1};
  const action=(label:string,id:string,press:()=>void,disabled=false,kind:'primary'|'secondary'|'danger'='secondary')=><TouchableOpacity
    testID={id} accessibilityRole="button" accessibilityState={{disabled}} disabled={disabled}
    onPress={press} style={[styles.action,{backgroundColor:kind==='primary'?palette.selectedSurface:colors.white},
      kind==='secondary'&&{borderColor:colors.gray200},disabled&&styles.disabled]}>
    <Text style={[text,styles.actionText,{color:kind==='primary'?palette.selectedText:kind==='danger'?palette.danger:palette.heading}]}>{label}</Text></TouchableOpacity>;
  const details=(record:VisitRecord)=><View style={styles.details}>
    <View style={styles.detail}><Text style={[styles.label,{color:colors.gray600}]}>{t('visit.place')}</Text>
      <Text style={[text,styles.value]}>{record.place}</Text></View>
    <View style={styles.detail}><Text style={[styles.label,{color:colors.gray600}]}>{t('visit.time')}</Text>
      <Text style={[text,styles.value]}>{record.timing}</Text></View>
  </View>;
  return <ProfileSection testID="visit-invitation" header={<View style={styles.header}>
    <Text style={[text,styles.title,{color:palette.heading}]}>{t('visit.title')}</Text>
    <InfoButton open={info.open} onPress={info.toggle} subject={t('visit.title')} testID="visit-info"/>
  </View>}>
    <InfoText open={info.open} text={t('visit.hint')} testID="visit-info-text"/>
    {snapshot?.incoming?.enabled && <View testID="visit-incoming" style={[styles.incoming,{borderColor:colors.gray200}]}>
      <Text style={[text,styles.status,{color:palette.accent}]}>{t('visit.incoming')}</Text>
      {details(snapshot.incoming)}
    </View>}
    {error && <View style={styles.content}><Text accessibilityLiveRegion="polite" style={[text,{color:colors.gray600}]}>{t('visit.unavailable')}</Text>
      {action(t('common.retry'),'visit-retry',()=>setRefresh(n=>n+1),busy)}</View>}
    {snapshot && !editing && <View style={styles.content}>
      <Text testID="visit-own-state" style={[text,styles.status,{color:snapshot.mine.enabled?palette.accent:colors.gray600}]}>{snapshot.mine.enabled?t('visit.enabled'):t('visit.off')}</Text>
      {snapshot.mine.enabled && details(snapshot.mine)}
      <View style={styles.actions}>{action(t('visit.edit'),'visit-edit',begin,busy,snapshot.mine.enabled?'secondary':'primary')}
        {snapshot.mine.enabled && action(t('visit.revoke'),'visit-revoke',()=>{save(false);},busy,'danger')}</View>
    </View>}
    {editing && snapshot && <View style={styles.editor}>
      <View style={styles.detail}><Text style={[styles.label,{color:colors.gray600}]}>{t('visit.place')}</Text>
      <TextInput testID="visit-place" accessibilityLabel={t('visit.place')} value={place} onChangeText={setPlace}
        maxLength={160} editable={!busy} multiline style={[text,profileFieldStyle(colors),styles.input]}/></View>
      <View style={styles.detail}><Text style={[styles.label,{color:colors.gray600}]}>{t('visit.time')}</Text>
      <TextInput testID="visit-time" accessibilityLabel={t('visit.time')} value={timing} onChangeText={setTiming}
        maxLength={160} editable={!busy} multiline style={[text,profileFieldStyle(colors),styles.input]}/></View>
      <View style={styles.actions}>{action(t('visit.save'),'visit-save',()=>{save(true);},busy||!place.trim()||!timing.trim(),'primary')}
        {action(t('common.cancel'),'visit-cancel',()=>setEditing(false),busy)}</View>
    </View>}
  </ProfileSection>;
}
const styles=StyleSheet.create({
  header:{flexDirection:'row',alignItems:'center',minHeight:44},title:{fontWeight:'600'},
  content:{gap:16,padding:6},status:{fontWeight:'500',lineHeight:23},
  details:{gap:12},detail:{gap:6,minWidth:0},label:{fontSize:13,lineHeight:19},value:{lineHeight:24},
  actions:{gap:8},
  action:{minHeight:48,minWidth:0,borderWidth:1,borderColor:'transparent',borderRadius:radius.control,
    paddingHorizontal:14,paddingVertical:12,alignItems:'center',justifyContent:'center'},
  actionText:{fontWeight:'500',textAlign:'center',lineHeight:23},disabled:{opacity:0.5},
  incoming:{gap:14,padding:6,paddingBottom:20,marginBottom:14,borderBottomWidth:StyleSheet.hairlineWidth},
  editor:{gap:16,padding:6},input:{textAlignVertical:'top',lineHeight:24},
});
